/**
 * Work going ahead on an estimate nobody has signed (issue #463).
 *
 * The estimate-vs-conversation pilot (2026-09-28, ten real jobs) found #1048
 * roofed and invoiced on an estimate that was still a draft: never sent, never
 * signed, one line reading "Install new shingles". Materials were picked up and
 * the crew was paid, and nothing anywhere said the customer had agreed to
 * anything. This is the part of that check that needs no AI: the app already
 * knows which estimates are signed and which have orders or an invoice on them.
 *
 * It warns and never blocks, the same as the quote check (Jonathon,
 * 2026-09-18). Plenty of small repairs are agreed on the roof; the point is
 * that somebody decides that on purpose rather than never finding out.
 *
 * Only orders somebody actually placed count. Every new lead gets draft
 * material and work orders the moment an estimate is built, so an unplaced
 * order says nothing about whether work is going ahead — counting them would
 * put this warning on nearly every job, which is how the quote check ended up
 * switched off (ADR 0190).
 *
 * A contract signed outside the app counts. A job with a signed date or an
 * uploaded contract, and no estimate signed in the app, was signed on paper,
 * and the paper covers its work; nothing here can say which estimate it was,
 * and telling somebody to "get it signed" when it is signed is the false alarm
 * that gets a check ignored. On production on 2026-09-28 that was #1013: a
 * finished roof with a signed date and no signature in the app. A job with a
 * signature in the app is judged estimate by estimate, so a repair added to a
 * signed roof (#1010) still warns.
 */

import {
  MATERIAL_ORDER_BASE_STATUS,
  WORK_ORDER_BASE_STATUS,
} from "./orderStatus.js";

/** An estimate, as far as whether the customer signed it. */
export interface UnsignedWorkEstimate {
  id: string;
  job_id: string;
  name: string | null;
  /** `approved` is signed; only the acceptance trigger writes it. */
  status: string | null;
}

/** An order, material or work, on an estimate. */
export interface UnsignedWorkOrder {
  estimate_id: string | null;
  status: string | null;
}

/** An invoice and every estimate it bills. */
export interface UnsignedWorkInvoice {
  status: string | null;
  estimate_ids: readonly string[];
}

/** A job, as far as whether a contract was signed outside the app. */
export interface UnsignedWorkJob {
  id: string;
  signed_contract_at?: string | null;
  signed_contract_path?: string | null;
}

export interface UnsignedWorkInput {
  /** Every estimate on the jobs, signed or not. */
  estimates: readonly UnsignedWorkEstimate[];
  /** The jobs, so a contract signed on paper is known about. */
  jobs?: readonly UnsignedWorkJob[];
  materialOrders?: readonly UnsignedWorkOrder[];
  workOrders?: readonly UnsignedWorkOrder[];
  invoices?: readonly UnsignedWorkInvoice[];
}

/** What has happened on an unsigned estimate, furthest along first. */
export type UnsignedWorkStep =
  | "materials_ordered"
  | "crew_scheduled"
  | "work_done"
  | "crew_paid"
  | "invoiced";

export interface UnsignedWork {
  estimateId: string;
  jobId: string;
  estimateName: string;
  steps: UnsignedWorkStep[];
  /** The sentence the job page shows. */
  warning: string;
}

/** How far a work order has got, in the order the checklist ticks them. */
const WORK_STEP: Record<string, UnsignedWorkStep> = {
  scheduled: "crew_scheduled",
  completed: "work_done",
  paid: "crew_paid",
};
const WORK_RANK: readonly UnsignedWorkStep[] = [
  "crew_scheduled",
  "work_done",
  "crew_paid",
];

const PHRASE: Record<UnsignedWorkStep, string> = {
  materials_ordered: "materials were ordered",
  crew_scheduled: "a crew is scheduled",
  work_done: "the work is done",
  crew_paid: "the crew was paid",
  invoiced: "an invoice was made",
};

/** "a", "a and b", "a, b and c". */
function listed(parts: readonly string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

function isSigned(estimate: UnsignedWorkEstimate): boolean {
  return estimate.status === "approved";
}

function wasPlaced(order: UnsignedWorkOrder): boolean {
  const status = order.status ?? MATERIAL_ORDER_BASE_STATUS;
  return status !== MATERIAL_ORDER_BASE_STATUS;
}

/** Estimates with a material order somebody placed. */
function orderedEstimates(orders: readonly UnsignedWorkOrder[]): Set<string> {
  const out = new Set<string>();
  for (const order of orders) {
    if (order.estimate_id && wasPlaced(order)) out.add(order.estimate_id);
  }
  return out;
}

/**
 * The furthest a crew has got on each estimate: "the crew was paid" already
 * says it was scheduled and done, so only the last rung is worth saying.
 */
function furthestCrew(
  orders: readonly UnsignedWorkOrder[],
): Map<string, UnsignedWorkStep> {
  const out = new Map<string, UnsignedWorkStep>();
  for (const order of orders) {
    const step = WORK_STEP[order.status ?? WORK_ORDER_BASE_STATUS];
    if (!order.estimate_id || !step) continue;
    const had = out.get(order.estimate_id);
    if (!had || WORK_RANK.indexOf(step) > WORK_RANK.indexOf(had)) {
      out.set(order.estimate_id, step);
    }
  }
  return out;
}

/** Estimates billed by an invoice that is not void. */
function invoicedEstimates(
  invoices: readonly UnsignedWorkInvoice[],
): Set<string> {
  const out = new Set<string>();
  for (const invoice of invoices) {
    if (invoice.status === "void") continue;
    for (const id of invoice.estimate_ids) out.add(id);
  }
  return out;
}

/**
 * Jobs whose contract was signed outside the app: a signed date or an upload,
 * and no estimate on the job signed in it.
 */
function signedOnPaper(input: UnsignedWorkInput): Set<string> {
  const signedInApp = new Set(
    input.estimates.filter(isSigned).map((estimate) => estimate.job_id),
  );
  const out = new Set<string>();
  for (const job of input.jobs ?? []) {
    const paper = Boolean(job.signed_contract_at || job.signed_contract_path);
    if (paper && !signedInApp.has(job.id)) out.add(job.id);
  }
  return out;
}

/**
 * One finding per unsigned estimate that has work going ahead on it, in the
 * order the estimates came in.
 */
export function unsignedWork(input: UnsignedWorkInput): UnsignedWork[] {
  const ordered = orderedEstimates(input.materialOrders ?? []);
  const crew = furthestCrew(input.workOrders ?? []);
  const invoiced = invoicedEstimates(input.invoices ?? []);
  const onPaper = signedOnPaper(input);

  const out: UnsignedWork[] = [];
  for (const estimate of input.estimates) {
    if (isSigned(estimate) || onPaper.has(estimate.job_id)) continue;
    const crewStep = crew.get(estimate.id);
    const steps: UnsignedWorkStep[] = [
      ...(ordered.has(estimate.id) ? ["materials_ordered" as const] : []),
      ...(crewStep ? [crewStep] : []),
      ...(invoiced.has(estimate.id) ? ["invoiced" as const] : []),
    ];
    if (steps.length === 0) continue;

    const name = estimate.name?.trim() || "An estimate";
    out.push({
      estimateId: estimate.id,
      jobId: estimate.job_id,
      estimateName: name,
      steps,
      warning: `Nobody has signed “${name}”, but ${listed(
        steps.map((step) => PHRASE[step]),
      )}. Get it signed, or check it was agreed.`,
    });
  }
  return out;
}
